import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, Pencil, Eye, X, User, Server, Key, Shield, Check } from "lucide-react";

const statusColors = {
  active: "bg-emerald-900/60 text-emerald-400 border border-emerald-700/50",
  inactive: "bg-amber-900/60 text-amber-400 border border-amber-700/50",
  deactivated: "bg-red-900/60 text-red-400 border border-red-700/50",
  dummy: "bg-zinc-800/60 text-zinc-400 border border-zinc-700/50",
  pending: "bg-blue-900/60 text-blue-400 border border-blue-700/50",
};

// Normalise fields that differ between brokers/APIs
function resolveField(user, ...keys) {
  for (const k of keys) {
    if (user[k] !== undefined && user[k] !== null && user[k] !== "") return user[k];
  }
  return null;
}

// ── View Details Modal ────────────────────────────────────────────────────────
function ViewUserModal({ user, onClose }) {
  if (!user) return null;

  const phone = resolveField(user, "phone_number", "phone");
  const email = resolveField(user, "email");
  const pan = resolveField(user, "pan_card", "pan");
  const dob = resolveField(user, "date_of_birth", "dob");
  const clientId = resolveField(user, "client_id");
  const name = resolveField(user, "name");
  const broker = resolveField(user, "broker");
  const status = resolveField(user, "status", "account_status");
  const source = resolveField(user, "source");

  const Section = ({ title, icon: Icon, children }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 pb-2 border-b border-zinc-800/60">
        <div className="w-6 h-6 rounded-md bg-zinc-800/80 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-zinc-400" />
        </div>
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{title}</p>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {children}
      </div>
    </div>
  );

  const Field = ({ label, value, mono = false }) => (
    <div className="space-y-0.5">
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm text-white ${mono ? "font-mono" : "font-medium"} break-all`}>
        {value || <span className="text-zinc-600 font-normal">—</span>}
      </p>
    </div>
  );

  const normalizedStatus = (status || "").toLowerCase();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] mx-4 bg-[#0f1116] border border-zinc-800/60 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/60 bg-[#0d0f18]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-zinc-800/80 border border-zinc-700/50 flex items-center justify-center">
              <User className="w-4.5 h-4.5 text-zinc-300" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base leading-tight">{name || "User Details"}</h2>
              {clientId && (
                <p className="text-zinc-500 text-xs font-mono mt-0.5">{clientId}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {status && (
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${statusColors[normalizedStatus] || statusColors.dummy}`}>
                {normalizedStatus || "pending"}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-800/60 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 sleek-scrollbar" style={{ scrollbarWidth: "thin" }}>

          {/* Personal Info */}
          <Section title="Personal Information" icon={User}>
            <Field label="Full Name" value={name} />
            <Field label="Client ID" value={clientId} mono />
            <Field label="Phone" value={phone} />
            <Field label="Email" value={email} />
            <Field label="PAN" value={pan} mono />
            <Field label="Date of Birth" value={dob} />
            {source && <Field label="Source / Referral" value={source} />}
          </Section>

          {/* Broker Info */}
          <Section title="Broker & Account" icon={Server}>
            <Field label="Broker" value={broker?.toUpperCase()} />
            <Field label="Account Status" value={normalizedStatus || "pending"} />
            {resolveField(user, "vendor_id") && <Field label="Vendor ID" value={resolveField(user, "vendor_id")} mono />}
            {resolveField(user, "api_key") && <Field label="API Key" value={resolveField(user, "api_key")} mono />}
          </Section>

          {/* Products */}
          {(user.product_is_optimus || user.product_is_pledgeplus || user.product_is_polaris || user.product_is_polaris_lite) && (
            <Section title="Products" icon={Shield}>
              <div className="col-span-2 flex flex-wrap gap-2">
                {user.product_is_optimus && (
                  <span className="px-2.5 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs font-medium">Optimus</span>
                )}
                {user.product_is_pledgeplus && (
                  <span className="px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-medium">PledgePlus</span>
                )}
                {user.product_is_polaris && (
                  <span className="px-2.5 py-1 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs font-medium">Polaris</span>
                )}
                {user.product_is_polaris_lite && (
                  <span className="px-2.5 py-1 rounded bg-pink-500/10 text-pink-400 border border-pink-500/20 text-xs font-medium">Polaris Lite</span>
                )}
              </div>
            </Section>
          )}

          {/* Proxy */}
          {resolveField(user, "proxy_host", "proxy_host_db") && (
            <Section title="Proxy" icon={Key}>
              <Field label="Host" value={resolveField(user, "proxy_host", "proxy_host_db")} mono />
              <Field label="Port" value={resolveField(user, "proxy_port")} mono />
              <Field label="Static IP" value={resolveField(user, "proxy_static_ip")} mono />
              <Field label="User" value={resolveField(user, "proxy_user")} mono />
              <Field label="Toggle" value={user.proxy_toggle === true ? "Enabled" : user.proxy_toggle === false ? "Disabled" : null} />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Table ────────────────────────────────────────────────────────────────
export default function UserTableCRM({ users = [], broker, onEdit }) {
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const [viewingUser, setViewingUser] = useState(null);

  // Always close the dropdown when the users list changes (data refresh)
  useEffect(() => {
    setOpenMenuIndex(null);
  }, [users]);

  // Close on any outside click or scroll via document/window listeners
  useEffect(() => {
    const close = (e) => {
      if (e.type === "mousedown") {
        if (e.target.closest(".crm-dropdown-menu") || e.target.closest(".crm-dropdown-trigger")) {
          return;
        }
      }
      setOpenMenuIndex(null);
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const toggleMenu = (index, e) => {
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = 84; // Approx height of the 2-item dropdown
    const spaceBelow = window.innerHeight - rect.bottom;
    
    // If there is not enough space below and enough space above, show above
    const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    const top = showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4;

    setMenuPos({
      top,
      right: window.innerWidth - rect.right,
    });
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  if (!users.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-800/60 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <p className="text-zinc-400 text-sm font-medium">No {broker} users found</p>
        <p className="text-zinc-600 text-xs mt-1">Use "Add User" to onboard a new client</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full overflow-auto sleek-scrollbar">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800/80 text-zinc-500 uppercase text-[11px] tracking-wider">
              <th className="px-4 py-3 text-left font-medium w-10">#</th>
              <th className="px-4 py-3 text-left font-medium">Client ID</th>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Phone</th>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">PAN</th>
              <th className="px-4 py-3 text-left font-medium">Product</th>
              <th className="px-4 py-3 text-center font-medium">Legality</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => {
              const phone = resolveField(user, "phone_number", "phone");
              const email = resolveField(user, "email");
              const pan = resolveField(user, "pan_card", "pan");
              const status = (resolveField(user, "status", "account_status") || "").toLowerCase();

              return (
                <tr
                  key={user.client_id || index}
                  className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors duration-150 group"
                >
                  <td className="px-4 py-3.5 text-zinc-600 text-[11px] font-mono select-none">{index + 1}</td>
                  <td className="px-4 py-3.5 font-mono text-[12px] text-zinc-300">{user.client_id || "—"}</td>
                  <td className="px-4 py-3.5 text-white font-medium">{user.name || "—"}</td>
                  <td className="px-4 py-3.5 text-zinc-400">{phone || "—"}</td>
                  <td className="px-4 py-3.5 text-zinc-400 max-w-[160px] truncate" title={email}>{email || "—"}</td>
                  <td className="px-4 py-3.5 font-mono text-xs text-zinc-400">{pan || "—"}</td>
                  <td className="px-4 py-3.5 text-zinc-400">
                    <div className="flex flex-wrap gap-1">
                      {user.product_is_optimus && (
                        <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-medium">Optimus</span>
                      )}
                      {user.product_is_pledgeplus && (
                        <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-medium">PledgePlus</span>
                      )}
                      {user.product_is_polaris && (
                        <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[10px] font-medium">Polaris</span>
                      )}
                      {user.product_is_polaris_lite && (
                        <span className="px-2 py-0.5 rounded bg-pink-500/10 text-pink-400 border border-pink-500/20 text-[10px] font-medium">Polaris Lite</span>
                      )}
                      {/* Fallback when no product flags */}
                      {!user.product_is_optimus && !user.product_is_pledgeplus && !user.product_is_polaris && !user.product_is_polaris_lite && (
                        <>
                          {user.product ? (
                            <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-medium">{user.product}</span>
                          ) : (
                            <>
                              {user.is_pledge_plus && (
                                <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-medium">PledgePlus</span>
                              )}
                              {user.is_pledgeplus_mini && (
                                <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-medium">PledgePlus Mini</span>
                              )}
                              {!user.is_pledge_plus && !user.is_pledgeplus_mini && (
                                <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-medium">Optimus</span>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    {user.is_legalities_complete ? (
                      <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-red-400 mx-auto" />
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${statusColors[status] || statusColors.dummy}`}>
                      {status || "pending"}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => toggleMenu(index, e)}
                      className="crm-dropdown-trigger p-1.5 rounded-md hover:bg-zinc-700/60 text-zinc-500 hover:text-zinc-200 transition-colors duration-150"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>

                    {openMenuIndex === index && createPortal(
                      <div
                        style={{ position: "fixed", top: menuPos.top, right: menuPos.right }}
                        className="crm-dropdown-menu z-[9999] w-44 bg-[#1c1f2e] border border-zinc-700/60 rounded-lg shadow-xl overflow-hidden"
                      >
                        <button
                          onClick={() => { setViewingUser(user); setOpenMenuIndex(null); }}
                          className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-xs text-zinc-300 hover:bg-zinc-700/50 hover:text-white transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View Details
                        </button>
                        <button
                          onClick={() => { onEdit && onEdit(user); setOpenMenuIndex(null); }}
                          className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-xs text-zinc-300 hover:bg-zinc-700/50 hover:text-white transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit User
                        </button>
                      </div>,
                      document.body
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* View Details Modal */}
      {viewingUser && (
        <ViewUserModal user={viewingUser} onClose={() => setViewingUser(null)} />
      )}
    </>
  );
}
